'use client';

import { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

// 2026-05-10 build 1262 — port the shared Modal frame to B1 zine.
// Veronika circled "Edit Needle" and said "this font doesn't fit"
// — the title bar was the last bit of the form modal still rendering
// in the default sans-semibold treatment while the body around it
// was already mono caps + display sans + Fraunces italic. Frame
// recipe lifted from YarnDetailModal's custom masthead (build 1251):
// cream paper #EDE6D2 with halftone dot overlay, 1px ink border,
// title split at first space — head in display caps 22px sans-800,
// tail in lowercase italic Fraunces 16px. Square corners. Square
// ink-bordered close button. Affects every Modal in the app
// (~14 callsites) — the YarnDetailModal already bypasses this
// component with its own custom frame, so it's unaffected.
function splitZineTitle(raw: string): { head: string; tail: string } {
  const space = raw.indexOf(' ');
  if (space === -1) return { head: raw, tail: '' };
  return { head: raw.slice(0, space), tail: raw.slice(space + 1) };
}

export default function Modal({ open, onClose, title, children }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const { head, tail } = splitZineTitle(title);

  return (
    <div
      ref={overlayRef}
      // 2026-05-10 build 1263 — see YarnDetailModal for the rationale.
      // Centered pop-up on every breakpoint with safe-area padding
      // all around, so the panel doesn't grow into the iPhone status
      // bar and the page background stays visible around it.
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{
        padding: 'max(env(safe-area-inset-top, 0px), 24px) 16px max(env(safe-area-inset-bottom, 0px), 24px)',
      }}
      onClick={(e) => e.target === overlayRef.current && onClose()}
    >
      <div className="absolute inset-0 bg-text-primary/20 backdrop-blur-sm" />
      {/* role="dialog" + aria-modal makes assistive tech treat the
          inner panel as a modal surface, AND gives Playwright a stable
          `getByRole('dialog')` handle so tests can scope clicks/fills
          to the modal's children without colliding with off-modal
          inputs. aria-labelledby points at the heading so the dialog's
          accessible name == its visible title. */}
      <div
        className="relative w-full max-w-lg max-h-[80vh] flex flex-col animate-[slideUp_0.25s_ease]"
        style={{
          zIndex: 60,
          backgroundColor: 'var(--paper-1)',
          backgroundImage: 'radial-gradient(circle, rgba(15,14,12,0.10) 1px, transparent 1.4px)',
          backgroundSize: '4px 4px',
          border: '1px solid var(--ink)',
          borderRadius: 0,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            padding: '12px 16px 10px',
            borderBottom: '1px solid var(--ink)',
            flexShrink: 0,
            gap: 12,
          }}
        >
          <h2
            id={titleId}
            style={{ margin: 0, color: 'var(--ink)', lineHeight: 1.05, flex: 1, minWidth: 0 }}
          >
            <span
              style={{
                fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
                fontWeight: 800,
                fontSize: 22,
                letterSpacing: '-0.02em',
                textTransform: 'uppercase',
                lineHeight: 0.95,
              }}
            >
              {head}
            </span>
            {tail && (
              <>
                {' '}
                <span
                  style={{
                    fontFamily: "'Fraunces', 'Times New Roman', serif",
                    fontStyle: 'italic',
                    fontWeight: 400,
                    fontSize: 16,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {tail.toLowerCase()}
                </span>
              </>
            )}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0,
              width: 32,
              height: 32,
              border: '1px solid var(--ink)',
              background: 'transparent',
              color: 'var(--ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4 pb-20 flex-1">
          {children}
        </div>
      </div>
      <style jsx>{`
        @keyframes slideUp {
          from { transform: translateY(16px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
